// src/store/slices/equationsSlice.ts
/**
 * Equations Redux Slice
 * 
 * Manages plotted equations on the coordinate grid.
 * Pattern follows existing expressionsSlice.ts structure.
 * 
 * Phase 2: Graphing Equations
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../types/redux';
import { PlotEquation, GradientTriangle, EquationIntersection, Point2D } from '../../types/coordinateGrid';
import { parseEquation, findIntersections } from '../../lib/coordinateGrid/equationParser';

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

export interface EquationWidgetState {
  isVisible: boolean;
  position: { x: number; y: number };
  mode: 'create' | 'edit';
  equationId: string | null;
  initialExpression: string;
}

export interface EquationsState {
  equations: PlotEquation[];
  intersections: EquationIntersection[];
  selectedEquationIds: string[];
  widget: EquationWidgetState;
  nextId: number;
  /** Currently active equation being edited */
  editingEquationId: string | null;
  /** Default styling for new equations */
  defaultStyle: {
    color: string;
    thickness: number;
    showLabel: boolean;
    showIntercepts: boolean;
    showGradient: boolean;
  };
}

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState: EquationsState = {
  equations: [],
  intersections: [],
  selectedEquationIds: [],
  widget: {
    isVisible: false,
    position: { x: 0, y: 0 },
    mode: 'create',
    equationId: null,
    initialExpression: '',
  },
  nextId: 1,
  editingEquationId: null,
  defaultStyle: {
    color: '#3B82F6', // Blue
    thickness: 2,
    showLabel: true,
    showIntercepts: false,
    showGradient: false,
  },
};

// Color palette for auto-assigning equation colors
const EQUATION_COLORS = [
  '#3B82F6', // Blue
  '#EF4444', // Red
  '#10B981', // Green
  '#F59E0B', // Amber
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#84CC16', // Lime
];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Recalculate all intersections between equations
 */
function recalculateIntersections(equations: PlotEquation[]): EquationIntersection[] {
  const intersections: EquationIntersection[] = [];
  
  for (let i = 0; i < equations.length; i++) {
    for (let j = i + 1; j < equations.length; j++) {
      const eq1 = equations[i];
      const eq2 = equations[j];
      
      if (!eq1.visible || !eq2.visible) {
        continue;
      }
      
      const parsed1 = parseEquation(eq1.expression);
      const parsed2 = parseEquation(eq2.expression);
      
      if (!parsed1.isValid || !parsed2.isValid) {
        continue;
      }
      
      const points = findIntersections(parsed1, parsed2);
      
      for (const point of points) {
        intersections.push({
          equationIds: [eq1.id, eq2.id],
          point,
          visible: true,
        });
      }
    }
  }
  
  return intersections;
}

// =============================================================================
// REDUX SLICE
// =============================================================================

export const equationsSlice = createSlice({
  name: 'equations',
  initialState,
  reducers: {
    // CRUD Operations
    addEquation: (state, action: PayloadAction<{ expression: string; style?: Partial<PlotEquation> }>) => {
      const parsed = parseEquation(action.payload.expression);
      if (!parsed.isValid) return;
      
      const colorIndex = state.equations.length % EQUATION_COLORS.length;
      const newEquation: PlotEquation = {
        id: `eq-${state.nextId}`,
        expression: action.payload.expression,
        type: parsed.type,
        coefficients: parsed.coefficients,
        color: EQUATION_COLORS[colorIndex],
        thickness: state.defaultStyle.thickness,
        visible: true,
        showLabel: state.defaultStyle.showLabel,
        showIntercepts: state.defaultStyle.showIntercepts,
        showGradient: state.defaultStyle.showGradient,
        gradientTriangles: [],
        isSelected: false,
        ...action.payload.style,
      };
      
      state.equations.push(newEquation);
      state.nextId++;
      
      // Recalculate intersections
      state.intersections = recalculateIntersections(state.equations);
    },
    
    updateEquation: (state, action: PayloadAction<{ id: string; updates: Partial<PlotEquation> }>) => {
      const equation = state.equations.find(eq => eq.id === action.payload.id);
      if (equation) {
        Object.assign(equation, action.payload.updates);
        
        // Re-parse if expression changed
        if (action.payload.updates.expression) {
          const parsed = parseEquation(action.payload.updates.expression);
          equation.type = parsed.type;
          equation.coefficients = parsed.coefficients;
        }
        
        // Recalculate intersections if expression or visibility changed
        if (action.payload.updates.expression !== undefined || action.payload.updates.visible !== undefined) {
          state.intersections = recalculateIntersections(state.equations);
        }
      }
    },
    
    deleteEquation: (state, action: PayloadAction<string>) => {
      state.equations = state.equations.filter(eq => eq.id !== action.payload);
      state.selectedEquationIds = state.selectedEquationIds.filter(id => id !== action.payload);
      
      // Remove related intersections
      state.intersections = state.intersections.filter(
        int => !int.equationIds.includes(action.payload)
      );
    },
    
    // Selection
    selectEquation: (state, action: PayloadAction<{ id: string; ctrlKey?: boolean }>) => {
      if (!action.payload.ctrlKey) {
        state.equations.forEach(eq => eq.isSelected = false);
        state.selectedEquationIds = [];
      }
      const equation = state.equations.find(eq => eq.id === action.payload.id);
      if (equation) {
        equation.isSelected = !equation.isSelected;
        if (equation.isSelected) {
          if (!state.selectedEquationIds.includes(equation.id)) {
            state.selectedEquationIds.push(equation.id);
          }
        } else {
          state.selectedEquationIds = state.selectedEquationIds.filter(id => id !== equation.id);
        }
      }
    },
    
    deselectAllEquations: (state) => {
      state.equations.forEach(eq => eq.isSelected = false);
      state.selectedEquationIds = [];
    },
    
    // Gradient triangles
    addGradientTriangle: (state, action: PayloadAction<{ equationId: string; point1: Point2D; point2: Point2D }>) => {
      const equation = state.equations.find(eq => eq.id === action.payload.equationId);
      if (equation && equation.type === 'linear') {
        equation.gradientTriangles.push({
          id: `gt-${Date.now()}-${Math.random()}`,
          point1: action.payload.point1,
          point2: action.payload.point2,
          color: equation.color,
          showLabels: true,
        });
      }
    },
    
    removeGradientTriangle: (state, action: PayloadAction<{ equationId: string; triangleId: string }>) => {
      const equation = state.equations.find(eq => eq.id === action.payload.equationId);
      if (equation) {
        equation.gradientTriangles = equation.gradientTriangles.filter(
          t => t.id !== action.payload.triangleId
        );
      }
    },
    
    // Visibility toggles
    toggleEquationVisibility: (state, action: PayloadAction<string>) => {
      const equation = state.equations.find(eq => eq.id === action.payload);
      if (equation) {
        equation.visible = !equation.visible;
        // Recalculate intersections when visibility changes
        state.intersections = recalculateIntersections(state.equations);
      }
    },
    
    toggleShowIntercepts: (state, action: PayloadAction<string>) => {
      const equation = state.equations.find(eq => eq.id === action.payload);
      if (equation) {
        equation.showIntercepts = !equation.showIntercepts;
      }
    },
    
    toggleShowGradient: (state, action: PayloadAction<string>) => {
      const equation = state.equations.find(eq => eq.id === action.payload);
      if (equation) {
        equation.showGradient = !equation.showGradient;
      }
    },
    
    // Scene loading
    loadEquationsState: (state, action: PayloadAction<Partial<EquationsState>>) => {
      // Ensure widget state is reset when loading
      const payload = { ...action.payload };
      if (payload.widget) {
        // Don't load widget state from persistence
        delete payload.widget;
      }
      
      Object.assign(state, { 
        ...initialState, 
        ...payload,
        // Preserve current widget state if not loading scene
        widget: state.widget 
      });
      // Recalculate intersections after loading
      state.intersections = recalculateIntersections(state.equations);
    },
    
    clearAllEquations: (state) => {
      state.equations = [];
      state.intersections = [];
      state.selectedEquationIds = [];
    },

    // Widget Management
    showEquationWidget: (state, action: PayloadAction<Partial<EquationWidgetState>>) => {
      state.widget = {
        ...state.widget,
        ...action.payload,
        isVisible: true,
      };
    },

    hideEquationWidget: (state) => {
      state.widget.isVisible = false;
      state.widget.equationId = null;
      state.widget.initialExpression = '';
    },
  },
});

// =============================================================================
// ACTIONS & SELECTORS
// =============================================================================

export const {
  addEquation,
  updateEquation,
  deleteEquation,
  selectEquation,
  deselectAllEquations,
  addGradientTriangle,
  removeGradientTriangle,
  toggleEquationVisibility,
  toggleShowIntercepts,
  toggleShowGradient,
  loadEquationsState,
  clearAllEquations,
  showEquationWidget,
  hideEquationWidget,
} = equationsSlice.actions;

// Selectors
export const selectAllEquations = (state: RootState) => state.equations.equations;
export const selectVisibleEquations = (state: RootState) => 
  state.equations.equations.filter(eq => eq.visible);
export const selectSelectedEquations = (state: RootState) =>
  state.equations.equations.filter(eq => eq.isSelected);
export const selectEquationIntersections = (state: RootState) => state.equations.intersections;
export const selectEquationWidget = (state: RootState) => state.equations.widget;

export default equationsSlice.reducer;

